import draftGrades from './draftGrades.json';

// Static editorial content for the Almanac home page. Keeping this outside
// the route component makes the page layout easier to review and change.

const DRAFT_TEAM_COUNT = draftGrades.teams.length;
const DRAFT_PICK_COUNT = draftGrades.draftedPickCount;
const robbyDraft = draftGrades.teams.find((team) => team.manager === 'Robby');
const robbyKeeper = robbyDraft.keeperVerdict[0];
const DRAFT_TEAM_COUNT_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'][DRAFT_TEAM_COUNT];

export const YEARBOOK_ENTRIES = [
  {
    name: 'Anna',
    role: 'The Final Boss',
    receipt: 'Three playoff championships, including back-to-back titles in 2024 and 2025. Tied with Kyle for most all-time.',
    assignment: 'Break the all-time ring tie without acting like the fourth trophy is inevitable.',
  },
  {
    name: 'Kyle',
    role: 'The Original Dynasty',
    receipt: 'Won 2014, 2015, and 2018. Now 92-83 career and tied with Anna for most rings all-time.',
    assignment: 'Prove the dynasty is not just a History Channel special.',
  },
  {
    name: 'Robby',
    role: 'The Commissioner',
    receipt: '2022 champion, 95-80 career, and 14-8 lifetime against A.J.',
    assignment: 'Make one ruling without becoming the main character.',
  },
  {
    name: 'A.J.',
    role: 'The Robby Problem',
    receipt: 'Two-time champion — took the inaugural 2013 title, then the 2021 nuclear blast. Still 8-14 vs Robby.',
    assignment: 'Make the rivalry look like a rivalry again.',
  },
  {
    name: 'Amir',
    role: 'The Quiet Resume',
    receipt: '2019 champion, 86-89 career, and rarely the loudest story.',
    assignment: "Float near .500, then ruin someone else's playoff math.",
  },
  {
    name: 'Kevin',
    role: 'The Glitch Champion',
    receipt: 'Won the 2023 championship by 5.30 points. Went 2-12 in both 2024 and 2025.',
    assignment: 'Explain how the trophy and the back-to-back collapses came from the same lab.',
  },
  {
    name: 'Ryan',
    role: 'The Almost Guy',
    receipt: 'Two regular-season crowns (2013, 2024), nine playoff trips, still zero rings. Missed the 2025 playoffs at 7-7.',
    assignment: 'Win the part that happens after being good.',
  },
  {
    name: 'Bryan',
    role: 'The Grinder',
    receipt: '84 career wins and two 10+ win regular seasons. Won the 2021 and 2025 regular-season crowns. Still no ring.',
    assignment: 'Stop submitting strong resumes with no jewelry attached.',
  },
  {
    name: 'Josh',
    role: 'Daddy, Allegedly',
    receipt: 'Went 11-3 in 2022, tied for the best regular-season record ever. Still zero rings.',
    assignment: 'Win the whole thing before Cheekwood becomes the whole scouting report.',
  },
  {
    name: 'Samuel',
    role: 'The Vibes Department',
    receipt: '46-63 across eight seasons since joining in 2018, plus the famous 0.01-point loss.',
    assignment: 'Lose less while remaining suspiciously peaceful.',
  },
];

export const NUMBER_TILES = [
  {
    kind: 'stat',
    span: 'hero',
    tone: 'gold',
    mark: 'top-rule',
    value: '11-3',
    unit: 'Best regular-season record',
    caption: 'Josh 2022, Anna 2024, and Ryan 2024 all went 11-3.',
    sub: 'Only Anna turned the 11-win season into a playoff championship.',
  },
  {
    kind: 'stat',
    span: 'hero',
    tone: 'cream',
    mark: 'tick',
    value: '0.01',
    unit: 'Winning margin · Ryan over Samuel',
    caption: 'Ryan beat Samuel by 0.01 fantasy points in Week 6, 2024.',
    sub: "Final score: Ryan 135.69, Samuel 135.68. This is the league's smallest recorded margin.",
  },
  {
    kind: 'split',
    span: 'medium',
    tone: 'paper',
    left: {
      label: 'Narrowest title game',
      value: '2.41',
      sub: 'Anna beat Amir 219.28 to 216.87 in the 2020 championship.',
    },
    right: {
      label: 'Widest title game',
      value: '139.76',
      sub: 'A.J. beat Bryan 263.77 to 124.01 in the 2021 championship.',
    },
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'dark',
    mark: 'tick',
    value: '11-6',
    unit: 'Head-to-head record · Robby vs Kevin',
    caption: 'Robby has beaten Kevin 11 times and lost 6 times.',
    sub: "That is a 64.7% win rate and still one of the league's cleanest ownership claims.",
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'cream',
    mark: 'left-rule',
    value: '8-14',
    unit: 'Head-to-head record · A.J. vs Robby',
    caption: 'A.J. has 8 wins and 14 losses against Robby.',
    sub: 'Every new matchup is either a comeback story or another exhibit.',
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'dark',
    mark: 'top-rule',
    value: '45',
    unit: 'Ringless active seasons',
    caption: 'Josh, Bryan, Ryan, and Samuel have played 45 combined seasons.',
    sub: 'They have made plenty of playoffs. They have won zero championships.',
  },
  {
    kind: 'quote',
    span: 'medium',
    tone: 'crimson',
    label: 'The group-chat thesis',
    text: 'Every stat is either a receipt, an excuse, or something someone refuses to acknowledge.',
    cite: 'Vol. XIV editorial policy',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'none',
    value: '162.33',
    unit: 'Biggest winning margin · Anna over Robby',
    caption: 'Anna won by 162.33 fantasy points in Week 9, 2015.',
    sub: 'Final score: Anna 276.75, Robby 114.42. Biggest blowout on record.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'tick',
    value: '875',
    unit: 'Regular-season games tracked',
    caption: 'The database includes 875 regular-season matchups from 2013-2025.',
    sub: 'Those games produced 311,256.64 total fantasy points.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'top-rule',
    value: '6 of 13',
    unit: 'Titles owned by Anna and Kyle',
    caption: 'Anna and Kyle own three playoff championships apiece.',
    sub: 'Everyone else has seven combined.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'left-rule',
    value: '301.55',
    unit: 'Single-week scoring record',
    caption: 'Kevin scored 301.55 points against Kyle in Week 13, 2023.',
    sub: 'Final score: Kevin 301.55, Kyle 170.16. Highest one-week team score.',
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'gold',
    mark: 'top-rule',
    value: '2013',
    unit: 'The inaugural ring · A.J.',
    caption: 'A.J. won the league\'s first championship in 2013, back when the league was still called La Liga.',
    sub: 'Kyle said the trophy case was wrong on Tape №3 and told Ryan to fix the website. Fixed. Two rings.',
  },
  // Bonus comedy stats validated against the league CSVs in 2026 QA pass.
  {
    kind: 'stat',
    span: 'medium',
    tone: 'cream',
    mark: 'tick',
    value: '0.03',
    unit: 'Second-closest game ever',
    caption: 'Amir beat Josh 173.33 to 173.30 in Week 11, 2019.',
    sub: 'A 0.03-point margin. Three one-hundredths. Three hundredths.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'tick',
    value: '0.10',
    unit: 'Third-closest game ever',
    caption: 'Robby beat A.J. 189.74 to 189.64 in Week 2, 2023.',
    sub: 'The rivalry adds another tenth-of-a-point receipt.',
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'dark',
    mark: 'top-rule',
    value: '293.36',
    unit: 'Second-highest single-week score',
    caption: 'Anna dropped 293.36 on A.J. in Week 13, 2015.',
    sub: 'Final score: Anna 293.36, A.J. 199.94. Two seasons before Kevin\'s 301.55.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'cream',
    mark: 'left-rule',
    value: '145.24',
    unit: 'Second-largest blowout',
    caption: 'Ryan beat Michael R by 145.24 in Week 13, 2014.',
    sub: 'The 2014 season still produced one of the biggest beatdowns on record.',
  },
  {
    kind: 'quote',
    span: 'medium',
    tone: 'gold',
    label: 'On the 0.01 game',
    text: 'A whole season can come down to whether someone\'s kicker hit a 47-yarder.',
    cite: 'Vol. XIV receipts desk',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'top-rule',
    value: '3',
    unit: 'Times Anna has won the playoff championship',
    caption: 'Anna has appeared in eight playoffs and won three of them — a 37.5% conversion rate.',
    sub: 'Back-to-back rings in 2024 and 2025. Tied with Kyle for most all-time.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: 'tick',
    value: '14',
    unit: 'A.J.\'s losses to Robby',
    caption: 'A.J. is 8-14 lifetime against Robby.',
    sub: 'Robby has won 64% of the meetings. The bit writes itself.',
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'paper',
    mark: 'tick',
    value: '0',
    unit: "Rings owned by Ryan, Josh, Bryan, Samuel",
    caption: 'Four active managers. 45 combined seasons. No trophies.',
    sub: 'Someone breaks the seal eventually.',
  },
  {
    kind: 'stat',
    span: 'hero',
    tone: 'crimson',
    mark: 'top-rule',
    value: '107.36',
    unit: '2025 title-game margin · Anna over Robby',
    caption: 'Anna beat Robby 214.83 to 107.47 in the 2025 championship.',
    sub: "Second-largest title-game blowout ever (A.J.'s 139.76 over Bryan in 2021 still tops it).",
  },
  {
    kind: 'stat',
    span: 'medium',
    tone: 'dark',
    mark: 'left-rule',
    value: '10-4',
    unit: "Bryan's 2025 record · regular-season champion (again)",
    caption: 'Bryan won the regular season at 10-4 and lost in the semifinals.',
    sub: 'Second 10+ win regular season for Bryan. Still zero rings.',
  },
  {
    kind: 'stat',
    span: 'small',
    tone: 'cream',
    mark: 'tick',
    value: '267.07',
    unit: "Commissioner's Army · league quarterfinal record",
    caption: 'Robby scored 267.07 in the 2025 quarterfinal vs Amir (165.57).',
    sub: 'Highest playoff-quarterfinal score in league history.',
  },
];

export const KEEPER_TILES = [
  {
    eyebrow: 'NEW RULE',
    headline: 'One keeper per manager',
    body: `Everyone carried one player from last season into 2026. All ${DRAFT_TEAM_COUNT_WORD} keepers were filed and priced at last year\u2019s round, including Robby\u2019s round-${robbyKeeper.round} ${robbyKeeper.player}.`,
    tone: 'gold',
    span: 'wide',
  },
  {
    eyebrow: 'THE COST',
    headline: 'Keep him, lose that round',
    body: 'Each manager paid the 2026 draft pick from the same round where his keeper was drafted in 2025. The completed board shows every charge.',
    tone: 'dark',
    span: 'tall',
  },
  {
    eyebrow: 'THE GAME',
    headline: 'Star power vs draft value',
    body: 'A first-round superstar was safe. Several late-round keepers were theft. The Registrar scored the result instead of the explanation.',
    tone: 'cream',
    span: 'small',
  },
  {
    eyebrow: 'OFFICIAL YAHOO CARD',
    headline: 'Week 1 is published',
    body: 'All five opening matchups are live. Keeper choices, drafted rosters, composite scores, and grades are now filed.',
    tone: 'crimson',
    span: 'wide',
  },
  {
    eyebrow: 'USEFUL RECEIPT',
    headline: '2026 draft picks are evidence',
    body: `The full ${DRAFT_PICK_COUNT}-pick board now shows the player, slot, manager, VORP, and value against the curve. The draft is no longer hypothetical.`,
    tone: 'dark',
    span: 'small',
  },
];

export const GROUP_CHAT_ARGUMENTS = [
  { eyebrow: 'CASE 01', headline: 'Is Anna already the final boss?', body: 'Three rings, including back-to-back titles, and she just dropped 214.83 on Robby in the title game. Kyle is tied with her at three.', tone: 'gold', span: 'wide' },
  { eyebrow: 'CASE 02', headline: 'Does Kyle still get dynasty privileges?', body: 'The resume says yes: 92-83 and three rings. The calendar says the last parade was in 2018, but he still shares the all-time lead with Anna.', tone: 'dark', span: 'small' },
  { eyebrow: 'CASE 03', headline: 'Was Kevin 2023 proof or a clerical error?', body: 'He won the title by 5.30, then went 2-12 in 2024 and 2-12 again in 2025. That is either magic or evidence tampering.', tone: 'cream', span: 'small' },
  { eyebrow: 'CASE 04', headline: 'Can A.J. make Robby stop saying 14-8?', body: 'A.J. is now 8-14 lifetime against Robby. The 2013 ledger made the bit worse, not better.', tone: 'crimson', span: 'wide' },
  { eyebrow: 'CASE 05', headline: 'Who escapes the ringless table?', body: 'Josh, Bryan, Ryan, and Samuel have 45 combined seasons and zero trophies. Bryan just lost the 2025 semis at 10-4 — strong resumes still no jewelry.', tone: 'dark', span: 'tall' },
  { eyebrow: 'CASE 06', headline: 'Will Samuel ever get the 0.01 back?', body: 'No. But the site will keep bringing it up, which is almost the same as closure.', tone: 'cream', span: 'small' },
  { eyebrow: 'CASE 07', headline: 'Is Josh actually 0.0%?', body: 'Kyle opened the 2026 board with Daddy last. AJ agreed on the air. The Cheekwood no-show is now an exhibit.', tone: 'crimson', span: 'wide' },
  { eyebrow: 'CASE 08', headline: 'Does Teich owe Amir a first?', body: 'Kyle wagered a first-round pick on whether his kids understood Deportivo\'s reply. Merritt said no. Fantasy court is in session.', tone: 'gold', span: 'small' },
  { eyebrow: 'CASE 09', headline: 'Is Bach actually 5.0%?', body: 'Kyle forgot to say the number on the Grinders tape, then confirmed 5.0% after. AJ still would not have him this high. Kevin says the dry draft is fake news.', tone: 'crimson', span: 'wide' },
  { eyebrow: 'CASE 10', headline: 'Is Doster covering payroll?', body: 'AJ put Bryan fourth and called it a diversification play: car washes, PE, tax burden if he wins. He bounces on draft night. He never misses hoops.', tone: 'gold', span: 'small' },
  { eyebrow: 'CASE 11', headline: 'Does AJ just want to win?', body: 'Kyle put him third at 7.3% and split the tape because it ran long. AJ answered: third is reasonable. High-variance club. Sat T-Law on purpose. After two titles it is about how you win, not whether.', tone: 'crimson', span: 'wide' },
  { eyebrow: 'CASE 12', headline: 'Did Teich show his hand at two?', body: 'Anna is 20.99%. Kyle wants her to pick her draft slot. Two members already told him no. AJ called the Phenoms tape the league-office tell.', tone: 'gold', span: 'small' },
  { eyebrow: 'CASE 13', headline: 'Is 50.01% a model or a bit?', body: 'Kyle put himself first, parked the car, and said he is the league. AJ: no surprise. Belichick — fastball or Chapel Hill Bill.', tone: 'crimson', span: 'wide' },
];

export const SHAME_CARDS = [
  {
    award: 'The Heartbreak Award',
    winner: 'Samuel',
    cardNo: '003',
    series: 'Vol. XIV · Almanac Originals',
    story: 'Samuel lost to Ryan 135.69 to 135.68 in Week 6, 2024. The 0.01-point gap is the closest regular-season finish in league history.',
  },
  {
    award: 'The Collapse Award',
    winner: 'Bryan',
    cardNo: '008',
    series: 'Vol. XIV · Almanac Originals',
    story: 'Bryan went 10-4 in 2021 (lost the title game 263.77 to 124.01) and 10-4 again in 2025 (lost the semifinal). Two regular-season crowns, zero rings.',
  },
  {
    award: 'The Daddy Issues Award',
    winner: 'Josh',
    cardNo: '011',
    series: 'Vol. XIV · Almanac Originals',
    story: 'Josh went 11-3 in 2022, tied for the best regular-season record ever, but lost the playoff semifinal to Anna 175.79 to 155.38. The nickname survived. The title did not.',
  },
  {
    award: 'The Cheekwood Award',
    winner: 'Josh',
    cardNo: '014',
    series: 'Vol. XIV · The Tape',
    story: 'Josh missed the 2025 draft for a Cheekwood event that was not a real concert. Kyle and AJ both entered it as league canon this week.',
  },
  {
    award: 'The 107 Award',
    winner: 'Robby',
    cardNo: '015',
    series: 'Vol. XIV · The Tape',
    story: 'Robby scored 107.47 in the 2025 championship against Anna\'s 214.83. AJ called the real-champion talk a clerical error. The ledger calls it the second-largest title-game margin ever.',
  },
  {
    award: 'The Mickey Mouse Award',
    winner: 'Kevin',
    cardNo: '016',
    series: 'Vol. XIV · The Tape',
    story: 'Kevin won the 2023 title by 5.30 points, then went 2-12 and 2-12. AJ reclassified the ring as a COVID-bubble championship. The trophy case still has to keep it.',
  },
  {
    award: 'The Dry Draft Award',
    winner: 'Kevin',
    cardNo: '017',
    series: 'Vol. XIV · The Tape',
    story: 'Kyle wants a documentary about Kevin staying sober at the 2025 draft. Kevin: Teich manifested it. The model line, which Kyle forgot to say on camera, is 5.0%.',
  },
  {
    award: 'The Payroll Award',
    winner: 'Bryan',
    cardNo: '018',
    series: 'Vol. XIV · The Tape',
    story: 'AJ ranked Doster a perfect fourth: cannot go lower, cannot go higher. Covering payroll. Bounces on draft night. Never misses hoops.',
  },
];

export const FALLBACK_TICKER = [
  'Best regular-season record: 11-3. Josh did it in 2022, Anna in 2024, and Ryan in 2024. Only Anna turned it into a championship.',
  'Closest regular-season game: Ryan beat Samuel 135.69 to 135.68 in Week 6, 2024, a 0.01-point margin.',
  'Largest title-game margin: A.J. beat Bryan 263.77 to 124.01 in the 2021 championship, a 139.76-point gap.',
  "Anna and Kyle share a 37.5% playoff conversion rate: 3 championships in 8 playoff appearances apiece, best among active managers.",
  "Narrowest championship: Anna beat Amir 219.28 to 216.87 in the 2020 final, a 2.41-point margin.",
  'Biggest regular-season blowout: Anna beat Robby 276.75 to 114.42 in Week 9, 2015, a 162.33-point margin.',
  'League database: 875 regular-season matchups from 2013-2025 and 311,256.64 total fantasy points.',
  'Championship count: 13 playoff titles awarded to 7 managers. Anna and Kyle own 6 of those titles combined.',
  '2025 title-game blowout: Anna beat Robby 214.83 to 107.47 — second-largest title-game margin (107.36) in league history.',
  "Robby's 107.47 in the 2025 championship is the lowest losing score in any title game on record.",
  "Bryan's 10-4 in 2025 was his second 10+ win regular season. Both ended without a ring.",
  "Anna's 2024 and 2025 rings made her a back-to-back champion and tied her with Kyle at three titles all-time.",
];
